import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import PropTypes from 'prop-types';
import { Progress, Select } from 'antd';
import { getOptionTimer } from './constants';
import styles from './styles.module.less';
import useStopWatch from '../../packages/utils/hooks/useStopWatch';
import { useIntl } from 'react-intl';
import IntlMessages from 'util/IntlMessages';
import classnames from 'classnames';
import { ActionButtons } from './components/ActionButtons';

const { Option } = Select;

/**PARAMS / PROPS
 * initValue : must be minutes / it is default value
 * times: must be an array of numbers , it going to manage the timer options
 * addManualRefresh: work to add manual button
 */

const SelectRefreshTimer = forwardRef(
  (
    {
      className = null,
      addManualRefresh = false,
      onRefresh,
      times = [],
      initValue,
      size = 'auto',
      manualButtonPos = 'left',
      pauseButton = false,
    },
    ref,
  ) => {
    const [timeSelected, setTimeSelected] = useState(initValue);
    const intl = useIntl();
    const placeholder = intl.formatMessage(
      { id: 'component.selectRefreshTimer.placeholder' },
      { time: timeSelected },
    );
    const timeInSeconds = timeSelected * 60;
    const OptionTimer = getOptionTimer(times);

    const {
      time: stopWatchTimer,
      resetTime,
      initTime,
      pauseTime,
      resumeTime,
      isPaused,
    } = useStopWatch(timeInSeconds);

    const timeInProgress = (stopWatchTimer / timeInSeconds) * 100;

    const handleChange = minutes => {
      setTimeSelected(minutes);
      resetTime();
    };

    const resetTimer = () => {
      resetTime();
    };

    useImperativeHandle(ref, () => ({
      reset: resetTimer,
      pause: pauseTime,
      resume: resumeTime,
    }));

    useEffect(() => {
      initTime();
    }, []);

    useEffect(() => {
      if (timeInProgress === 100) onRefresh(true);
      if (timeInProgress === 0) onRefresh(false);
    }, [timeInProgress]);

    return (
      <div style={{ width: size }} className={classnames(styles.container, className)}>
        {addManualRefresh && manualButtonPos === 'left' && (
          <ActionButtons
            onRefresh={onRefresh}
            pauseButton={pauseButton}
            isPaused={isPaused}
            resumeTime={resumeTime}
            pauseTime={pauseTime}
          />
        )}
        <div className={styles.manualButtonC}>
          <div>
            <div className="gx-w-100 gx-flex-1">
              <Select
                value={timeSelected}
                onChange={minutes => handleChange(Number(minutes))}
                placeholder={placeholder}
                style={{ width: '100%' }}
              >
                {OptionTimer.map(({ label, value }) => (
                  <Option key={value} value={value}>
                    <IntlMessages
                      id="component.selectRefreshTimer.placeholder"
                      values={{ time: label }}
                    />
                  </Option>
                ))}
              </Select>
            </div>
            <div className={classnames('gx-w-100 gx-flex-1', styles.progressBarC)}>
              <Progress
                className={styles.customProgress}
                showInfo={false}
                percent={timeInProgress}
                status="active"
              />
            </div>
          </div>
        </div>
        {addManualRefresh && manualButtonPos === 'right' && (
          <ActionButtons
            onRefresh={onRefresh}
            pauseButton={pauseButton}
            isPaused={isPaused}
            resumeTime={resumeTime}
            pauseTime={pauseTime}
          />
        )}
      </div>
    );
  },
);

SelectRefreshTimer.propTypes = {
  className: PropTypes.string,
  addManualRefresh: PropTypes.bool,
  onRefresh: PropTypes.func.isRequired,
  times: PropTypes.arrayOf(PropTypes.number),
  initValue: PropTypes.number,
  size: PropTypes.string,
};

export default SelectRefreshTimer;
